import { useState, useEffect } from 'react'
import message from 'antd/lib/message'
import Form from 'antd/lib/form'

import { commonHelper } from 'helpers'
import { useGetCustomers, useGetLiability } from 'services/clients/clients.service'

import { commonUtils, eventManager } from 'utils'
import { addEditOtherLiabilitiesMutation } from 'gql/client'
import { useCustomMutation } from 'services/shared/mutation'
import { AddEditOtherLiabilitiesPayload } from 'services/shared/mutation.payload'
import { getCustomersOptions } from 'utils/getCustomersOptions'

const initialState = {
  isOpen: false,
  data: { _id: '', isEdit: true },
  cb: { confirm: () => null, cancel: () => null },
  loading: false,
}

const useEditLiabilityModal = () => {
  const [form] = Form.useForm()
  const addEditOtherLiabilities = useCustomMutation<AddEditOtherLiabilitiesPayload>({
    mutation: addEditOtherLiabilitiesMutation,
    refetchQueries: ['getOtherLiabilities'],
  })
  const urlParams = new URLSearchParams(window.location.search)
  const idNumber = Number(urlParams.get('idNumber'))
  // this should be fetched to use this for editing as well, new it only adds new
  const [modalState, setModalState] = useState(initialState)
  const liabilityToEdit = modalState?.data?._id
  const { liability, loading: initialDataLoading } = useGetLiability({
    idNumber,
    liabilityId: liabilityToEdit,
    skip: !modalState.isOpen || !modalState.data.isEdit,
  })
  const { data: customersData } = useGetCustomers(idNumber)
  const [loading, setLoading] = useState(initialDataLoading)
  const [formData, setFormData] = useState({
    liabilityHolder: liability?.liabilityHolder,
    liabilityType: liability?.liabilityType,
    balance: liability?.balance,
    monthlyCost: liability?.monthlyCost,
    remainingTerm: liability?.remainingTerm,
    willMonthlyCostChange: liability?.willMonthlyCostChange,
    monthlyCostChangePeriod: liability?.monthlyCostChangePeriod,
    monthlyCostWillChangeTo: liability?.monthlyCostWillChangeTo,
    earlyRepaymentCharges: liability?.earlyRepaymentCharges,
    interestRate: liability?.interestRate,
    planToRepay: liability?.planToRepay,
    description: liability?.description,
  })

  const customersOptions = getCustomersOptions(customersData, true)

  useEffect(() => {
    setLoading(initialDataLoading)
    const newData = commonUtils.removeTypeNameFromObject(liability)
    delete newData._id
    setFormData({ ...newData })
    form.setFieldsValue({ ...newData })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialDataLoading])

  const clearFormData = () => {
    setFormData((prevState) => commonUtils.resetFormData(prevState))
    form.setFieldsValue(commonUtils.resetFormData(formData))
  }

  const handleCustomSelectChange = (name: string, value: string) => {
    setFormData((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleAntdFormInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name
    let value = e.target.value
    let formattedValue: string | boolean = value

    if (name === 'planToRepay' || name === 'willMonthlyCostChange') {
      if (value === 'true') {
        formattedValue = true
      } else if (value === 'false') {
        formattedValue = false
      }
    }
    setFormData((prevState) => ({
      ...prevState,
      [name]: formattedValue,
    }))
  }

  const handleCustomTextAreaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setFormData((prevState) => ({
      ...prevState,
      [e.target.name]: e.target.value,
    }))
  }

  useEffect(() => {
    eventManager.addListener('edit-liability-modal', (data: any, cb: any) => {
      setModalState((state) => ({
        ...state,
        isOpen: data !== undefined,
        data,
        cb,
      }))
    })
  }, [setModalState])

  const handleClose = () => {
    setModalState((state) => ({ ...state, loading: false }))
    eventManager.closeEditLiabilityModal()
  }

  const handleCancelClick = async () => {
    try {
      setModalState((state) => ({ ...state, loading: true }))
      if (modalState.cb.cancel) {
        await modalState.cb.cancel()
      }
    } catch (err) {
      // console.error(err)
    }
    handleClose()
    clearFormData()
  }

  const handleSubmit = async () => {
    const { errors } = await addEditOtherLiabilities({
      idNumber,
      liabilityToEdit,
      values: {
        ...formData,
        balance: Number(formData.balance),
        monthlyCost: Number(formData.monthlyCost),
        remainingTerm: Number(formData.remainingTerm),
        monthlyCostWillChangeTo: Number(formData.monthlyCostWillChangeTo),
        earlyRepaymentCharges: Number(formData.earlyRepaymentCharges),
        interestRate: Number(formData.interestRate),
      },
    })
    if (errors?.length) {
      const errMessage = commonHelper.parseGraphqlErrorMessage(errors)
      message.error(errMessage)
      return
    }
    message.success('Operation finished successfully')
  }

  const handleConfirmClick = async () => {
    form.validateFields().then(async () => {
      try {
        setModalState((state) => ({ ...state, loading: true }))
        await handleSubmit()
        clearFormData()
        if (modalState.cb.confirm) {
          await modalState.cb.confirm()
        }
      } catch (err) {
        // console.error(err)
      }
      setModalState((state) => ({ ...state, loading: false }))
      handleClose()
    })
  }

  return {
    loading,
    modalState,
    handleCancelClick,
    handleConfirmClick,
    formData,
    handleCustomSelectChange,
    handleAntdFormInputChange,
    handleCustomTextAreaChange,
    form,
    customersOptions,
  }
}

export default useEditLiabilityModal
